import React, { useState } from 'react';
import { Spinner, ErrorBanner } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api from '../../utils/api';

export default function InventoryComparePage({ account, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | mismatched

  const load = async () => {
    console.log('[InventoryCompare] load() called, fetching:', `/api/integrations/shopify/${account.id}/compare`);
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get(`/api/integrations/shopify/${account.id}/compare`);
      console.log('[InventoryCompare] response:', res);
      setData(res);
    } catch (err) {
      console.error('[InventoryCompare] error:', err);
      setError(errMsg(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    console.log('[InventoryCompare] mounting, account:', account?.id, account?.name);
    load();
  }, [account.id]); // eslint-disable-line

  const results = data?.results || [];
  const mismatched = results.filter(r => !r.match);
  const filtered = filter === 'mismatched' ? mismatched : results;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-sm" onClick={onClose}><i className="ti ti-arrow-left" /> Back</button>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Inventory Comparison</h2>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{account.name} — {account.store_url}</div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-sm btn-primary" onClick={load} disabled={loading}>
          <i className="ti ti-refresh" /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && <Spinner />}
      {!loading && data && (
        <>
          {/* Summary bar */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div className="card" style={{ padding: '10px 16px', flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{results.length}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Total products</div>
            </div>
            <div className="card" style={{ padding: '10px 16px', flex: 1, borderColor: mismatched.length ? 'var(--color-danger)' : undefined }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: mismatched.length ? 'var(--color-danger)' : 'var(--color-success)' }}>{mismatched.length}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Mismatched</div>
            </div>
            <div className="card" style={{ padding: '10px 16px', flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-success)' }}>{results.length - mismatched.length}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>In sync</div>
            </div>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : ''}`} onClick={() => setFilter('all')}>
              All ({results.length})
            </button>
            <button className={`btn btn-sm ${filter === 'mismatched' ? 'btn-primary' : ''}`} onClick={() => setFilter('mismatched')}>
              Mismatched ({mismatched.length})
            </button>
          </div>

          {/* Table */}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th style={{ textAlign: 'right' }}>StockForge qty</th>
                  <th style={{ textAlign: 'right' }}>Shopify qty</th>
                  <th style={{ textAlign: 'right' }}>Difference</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>
                    {filter === 'mismatched' ? 'All quantities match!' : 'No products found'}
                  </td></tr>
                )}
                {filtered.map(r => {
                  const diff = r.shopify_qty != null ? r.stockforge_qty - r.shopify_qty : null;
                  return (
                    <tr key={r.id} style={{ background: !r.match ? 'var(--red-light)' : undefined }}>
                      <td style={{ fontWeight: 500 }}>
                        {r.name}
                        {r.is_kit && <span className="badge badge-blue" style={{ marginLeft: 6, fontSize: 10 }}>Bundle</span>}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.sku}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt.number(r.stockforge_qty)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {r.error
                          ? <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Error</span>
                          : r.shopify_qty != null ? fmt.number(r.shopify_qty) : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {diff != null && diff !== 0 && (
                          <span style={{ color: diff > 0 ? 'var(--color-danger)' : 'var(--color-warning)', fontWeight: 500 }}>
                            {diff > 0 ? '+' : ''}{fmt.number(diff)}
                          </span>
                        )}
                        {diff === 0 && <span style={{ color: 'var(--color-success)' }}>—</span>}
                        {diff == null && '—'}
                      </td>
                      <td>
                        {r.error
                          ? <span className="badge badge-crit" title={r.error}>Error</span>
                          : r.match
                            ? <span className="badge badge-ok">In sync</span>
                            : <span className="badge badge-crit">Mismatch</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
